import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  CheckIcon,
  CopyIcon,
  CopyPlusIcon,
  FlaskConicalIcon,
  PencilIcon,
  SearchIcon,
  Trash2Icon,
} from 'lucide-react';
import { IconButton } from '../components/IconButton.js';
import { Button } from '../ui/Button.js';
import { Callout } from '../ui/Callout.js';
import { Combobox } from '../ui/Combobox.js';
import { ConfirmDialog } from '../ui/ConfirmDialog.js';
import { DataRow, DataTable, RowActions, TableCell, TableHead } from '../ui/DataTable.js';
import { Dialog } from '../ui/Dialog.js';
import { EmptyState } from '../shell/EmptyState.js';
import { formatCount, formatRelativeTime } from '../lib/format.js';
import { useAuth } from '../auth/useAuth.js';
import { useGroups } from '../groups/queries.js';
import { AddUrlDialog } from './AddUrlDialog.js';
import { CloneUrlDialog } from './CloneUrlDialog.js';
import { StatusMenu } from './StatusMenu.js';
import { UrlIdentityForm } from './UrlIdentityForm.js';
import { rememberUrlsList } from './listLocation.js';
import { useDeleteUrl, useUpdateUrl, useUrls, type UrlRow } from './queries.js';

/** How long a row's copy button stays in its confirmed state. */
const COPIED_NOTICE_MS = 1500;

/** Radix rejects an empty item value, so "no filter" needs a word of its own. */
const ALL_GROUPS = 'all';

/**
 * Whether a URL matches what was typed.
 *
 * Client-side, because `GET /api/urls` already returns every row for the
 * chosen group in one response — the screen has the whole answer in memory, so
 * a round trip per keystroke would only make filtering slower and less
 * certain. Four fields are searched: the URL as displayed, the operator's
 * label for it, the hostname on its own (so a bare hostname finds every URL on
 * it), and the group name (so a search still works with the picker on "all").
 */
export function matchesUrlQuery(row: UrlRow, rawQuery: string): boolean {
  const query = rawQuery.trim().toLowerCase();
  if (query === '') return true;

  return [row.fullUrl, row.label, row.hostname, row.groupName].some((field) =>
    field.toLowerCase().includes(query),
  );
}

/**
 * The query string a filter change produces.
 *
 * Extracted from the component so it can be tested without mounting a Radix
 * listbox: that costs seconds under jsdom, and the rule worth pinning down —
 * change one filter, leave the other alone, drop the parameter entirely at its
 * default — is a pure function of the parameters and the new value.
 */
export function nextFilterParams(
  current: URLSearchParams,
  key: string,
  value: string,
  clearWhen: string,
): URLSearchParams {
  const params = new URLSearchParams(current);
  if (value === clearWhen) params.delete(key);
  else params.set(key, value);
  return params;
}

export function UrlsScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const [adding, setAdding] = useState(false);
  /*
   * The row each dialog is about, rather than a boolean per dialog.
   *
   * These actions used to live on the single URL page, one URL at a time, so a
   * flag was enough. On a listing the question is always "which row", and
   * holding the row itself means the dialog keeps showing the URL it was
   * opened for even as the table refetches underneath it.
   */
  const [editing, setEditing] = useState<UrlRow | null>(null);
  const [cloning, setCloning] = useState<UrlRow | null>(null);
  const [deleting, setDeleting] = useState<UrlRow | null>(null);
  const [copied, setCopied] = useState<number | null>(null);
  const canEdit = user?.role === 'admin';

  const updateUrl = useUpdateUrl();
  const deleteUrl = useDeleteUrl();

  /* The filter lives in the URL rather than in state so a filtered view is a
     shareable link and the Back button steps through filters. */
  const requested = Number(search.get('group') ?? '');
  const groupId = Number.isInteger(requested) && requested > 0 ? requested : undefined;

  /* The text filter lives in the URL for the same reason the group does: a
     filtered view is a link worth sending someone. */
  const query = search.get('q') ?? '';

  const urls = useUrls(groupId);
  const groups = useGroups();
  const all = urls.data?.urls ?? [];
  const rows = useMemo(() => all.filter((row) => matchesUrlQuery(row, query)), [all, query]);

  /* Remembered so the URL page's back link returns here with this filter
     still applied, rather than to the top of eight hundred rows. */
  useEffect(() => {
    rememberUrlsList(search.toString() === '' ? '' : `?${search.toString()}`);
  }, [search]);

  function setParam(key: string, value: string, clearWhen: string) {
    setSearch(nextFilterParams(search, key, value, clearWhen), { replace: true });
  }

  return (
    <section aria-labelledby="urls-heading" className="flex flex-col gap-(--space-5)">
      <header className="flex flex-wrap items-end justify-between gap-(--space-4)">
        <div>
          <p className="text-[length:var(--text-micro)] uppercase tracking-[0.18em] text-ink-subtle">
            Configuration
          </p>
          <h1 id="urls-heading" className="mt-(--space-1) text-[length:var(--text-display)]">
            URLs
          </h1>
        </div>
        {/* One row, one height, every label above its control — the same
            arrangement as Reports, so the two screens do not each invent their
            own header. `items-end` keeps the unlabelled button on the same
            bottom edge as the labelled controls. */}
        <div className="flex flex-wrap items-end gap-(--space-3)">
          {/* Search first, group second: with no group chosen the text box is
              the faster way into a specific URL, and it is the control the
              operator reaches for most. */}
          <div className="flex flex-col gap-(--space-1)">
            <label
              htmlFor="urls-search"
              className="text-[length:var(--text-small)] font-bold text-ink"
            >
              Search
            </label>
            <div className="relative">
              <SearchIcon
                aria-hidden="true"
                className="pointer-events-none absolute left-(--space-2) top-1/2 size-4 -translate-y-1/2 text-ink-subtle"
              />
              <input
                id="urls-search"
                type="search"
                spellCheck={false}
                autoComplete="off"
                placeholder="URL, label or group"
                className="h-(--control-height) w-[18rem] max-w-full rounded-(--radius-md) border border-input bg-card pl-(--space-6) pr-(--space-3) text-[length:var(--text-small)] text-ink placeholder:text-ink-subtle"
                value={query}
                onChange={(event) => setParam('q', event.target.value, '')}
              />
            </div>
          </div>

          {/* A combobox rather than a select: an install has dozens of groups,
              and clearing the filter is a press of the X rather than hunting
              for an "All groups" row in a long list. */}
          <Combobox
            label="Group"
            className="w-[14rem]"
            value={groupId === undefined ? null : String(groupId)}
            options={(groups.data?.groups ?? []).map((group) => ({
              value: String(group.id),
              label: group.name,
            }))}
            onChange={(next) => setParam('group', next ?? ALL_GROUPS, ALL_GROUPS)}
            placeholder="All groups"
            searchPlaceholder="Filter groups…"
            emptyText="No group matched"
          />

          {canEdit && (
            <Button variant="primary" onClick={() => setAdding(true)}>
              Add URL
            </Button>
          )}
        </div>
      </header>

      {urls.isError && (
        <Callout tone="danger">
          {urls.error instanceof Error ? urls.error.message : 'Could not load URLs'}
        </Callout>
      )}

      {urls.isLoading && (
        <p role="status" className="text-ink-subtle">
          Loading URLs…
        </p>
      )}

      {/* Only while a search is narrowing the list: on an unfiltered listing the
          table is the count, and a line saying so on every visit is noise. */}
      {urls.data !== undefined && query.trim() !== '' && rows.length > 0 && (
        <p role="status" className="text-[length:var(--text-small)] text-ink-muted">
          {formatCount(rows.length)} of {formatCount(all.length)} URLs match “{query.trim()}”.
        </p>
      )}

      {urls.data !== undefined && rows.length === 0 && query.trim() !== '' && (
        <EmptyState
          title="Nothing matched that search"
          description={`No URL here contains “${query.trim()}”. Searching looks at the URL, its label, its hostname and its group${groupId === undefined ? '' : ' — and this list is narrowed to one group'}.`}
          action={
            <Button variant="ghost" onClick={() => setParam('q', '', '')}>
              Clear search
            </Button>
          }
        />
      )}

      {urls.data !== undefined && all.length === 0 && query.trim() === '' && (
        <EmptyState
          title={groupId === undefined ? 'No URLs yet' : 'No URLs in this group'}
          description={
            groupId === undefined
              ? 'A URL is one hostname and path clients link to, splitting its traffic across destinations. Add the first one and the domain it needs will be created with it.'
              : 'This group has no URLs configured. Add one, or switch the filter back to all groups.'
          }
          /* No action under the filtered message: the header's Add URL button
             is right above it, and "your first URL" would be untrue of a view
             that is merely filtered. */
          action={
            canEdit && groupId === undefined ? (
              <Button variant="primary" onClick={() => setAdding(true)}>
                Add your first URL
              </Button>
            ) : undefined
          }
        />
      )}

      {rows.length > 0 && (
        <DataTable
          caption="Configured URLs. Last miss is recorded against the hostname, so URLs sharing a hostname report the same value."
          head={
            <>
              <TableHead scope="col">URL</TableHead>
              <TableHead scope="col">Group</TableHead>
              <TableHead scope="col" className="text-right">
                Targets
              </TableHead>
              <TableHead scope="col">Status</TableHead>
              <TableHead scope="col" className="text-right">
                Last redirect
              </TableHead>
              {/*
                Named for the domain, not the URL. `traffic_miss_minute` records
                a domain and a reason but no entry — a miss means nothing
                matched, so there is no entry to attribute it to. Two URLs on
                one hostname therefore show the same value, and a bare "Last
                miss" would read as a per-URL figure.
              */}
              <TableHead scope="col" className="text-right">
                Last miss (domain)
              </TableHead>
              {/* Named, not blank. A column of icons still needs a header for
                  anyone reading the table by row and column rather than by
                  eye. */}
              <TableHead scope="col" className="text-right">
                Actions
              </TableHead>
            </>
          }
        >
          {rows.map((row) => (
            <DataRow
              key={row.entryId}
              onActivate={() => void navigate(`/urls/${row.entryId}`)}
              activateLabel={`Open ${row.fullUrl}`}
            >
              <TableCell className="py-(--space-3)">
                <span className="font-mono text-[length:var(--text-small)] font-bold text-ink">
                  {row.fullUrl}
                </span>
                {/* Omitted entirely when empty: most URLs carry no label, and a
                    muted placeholder on every row would be pure noise. */}
                {row.label !== '' && (
                  <p className="mt-(--space-1) max-w-[60ch] text-[length:var(--text-small)] font-medium text-ink-muted">
                    {row.label}
                  </p>
                )}
              </TableCell>
              <TableCell className="py-(--space-3) text-ink-muted">{row.groupName}</TableCell>
              <TableCell className="py-(--space-3) text-right tabular-nums">
                {formatCount(row.enabledTargetCount)} of {formatCount(row.targetCount)}
              </TableCell>
              {/* The badge is also the control. `RowActions` swallows the
                  click so opening the menu never also opens the URL. */}
              <TableCell className="py-(--space-3)">
                {/* Swallows the click itself rather than borrowing
                    `RowActions`, which right-aligns its cluster: this cell is
                    left-aligned under a left-aligned header. */}
                <div
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  <StatusMenu
                    entryId={row.entryId}
                    domainId={row.domainId}
                    label={row.label}
                    path={row.path}
                    enabled={row.enabled}
                    canEdit={canEdit}
                    urlForLabel={row.fullUrl}
                  />
                </div>
              </TableCell>
              <TableCell className="py-(--space-3) text-right tabular-nums text-ink-muted">
                {formatRelativeTime(row.lastSeenAt)}
              </TableCell>
              <TableCell className="py-(--space-3) text-right tabular-nums text-ink-muted">
                {formatRelativeTime(row.lastMissAt)}
              </TableCell>

              {/*
                The quick actions, on the row.
                They used to be on the single URL page, which meant three clicks
                and a page load to copy a link or delete a stale one — and put
                them somewhere you could only act on the URL you had already
                gone looking for. `RowActions` swallows the click so none of
                them also opens the row.
              */}
              <TableCell className="py-(--space-3)">
                <RowActions>
                  <IconButton
                    label={copied === row.entryId ? 'URL copied' : `Copy ${row.fullUrl}`}
                    /* Short under the pointer, full for a screen reader: the
                       operator hovering this button can already see which row
                       they are on. */
                    tooltip={copied === row.entryId ? 'Copied' : 'Copy'}
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(row.fullUrl);
                        setCopied(row.entryId);
                        setTimeout(() => setCopied(null), COPIED_NOTICE_MS);
                      } catch {
                        // Clipboard access can be denied; the URL is on screen anyway.
                      }
                    }}
                  >
                    {copied === row.entryId ? (
                      <CheckIcon aria-hidden="true" className="text-ok" />
                    ) : (
                      <CopyIcon aria-hidden="true" />
                    )}
                  </IconButton>
                  {/* A viewer gets this one too: testing a URL changes nothing,
                      and "where does this actually send people right now" is
                      the question a read-only operator most often has. */}
                  <IconButton
                    label={`Test ${row.fullUrl}`}
                    tooltip="Test"
                    onClick={() =>
                      void navigate(`/resolve?url=${encodeURIComponent(row.fullUrl)}`)
                    }
                  >
                    <FlaskConicalIcon aria-hidden="true" />
                  </IconButton>
                  {canEdit && (
                    <>
                      <IconButton
                        label={`Edit ${row.fullUrl}`}
                        tooltip="Edit"
                        onClick={() => setEditing(row)}
                      >
                        <PencilIcon aria-hidden="true" />
                      </IconButton>
                      <IconButton
                        label={`Clone ${row.fullUrl}`}
                        tooltip="Clone"
                        onClick={() => setCloning(row)}
                      >
                        <CopyPlusIcon aria-hidden="true" />
                      </IconButton>
                      <IconButton
                        label={`Delete ${row.fullUrl}`}
                        tooltip="Delete"
                        side="left"
                        className="text-danger hover:bg-(--color-danger-quiet) hover:text-danger"
                        onClick={() => setDeleting(row)}
                      >
                        <Trash2Icon aria-hidden="true" />
                      </IconButton>
                    </>
                  )}
                </RowActions>
              </TableCell>
            </DataRow>
          ))}
        </DataTable>
      )}

      <AddUrlDialog
        open={adding}
        onClose={() => setAdding(false)}
        {...(groupId === undefined ? {} : { defaultGroupId: groupId })}
        onCreated={(entryId) => void navigate(`/urls/${entryId}`)}
      />

      <Dialog open={editing !== null} title="Edit URL" onClose={() => setEditing(null)}>
        {editing !== null && (
          <UrlIdentityForm
            currentHostname={editing.hostname}
            initial={{
              url: `${editing.hostname}${editing.path}`,
              label: editing.label,
              enabled: editing.enabled,
            }}
            submitLabel="Save URL"
            onCancel={() => setEditing(null)}
            onSubmit={async (input) => {
              await updateUrl.mutateAsync({
                entryId: editing.entryId,
                domainId: editing.domainId,
                input,
              });
              setEditing(null);
            }}
          />
        )}
      </Dialog>

      {cloning !== null && (
        <CloneUrlDialog
          open
          source={{
            entryId: cloning.entryId,
            domainId: cloning.domainId,
            hostname: cloning.hostname,
            path: cloning.path,
            pathNorm: cloning.pathNorm,
            label: cloning.label,
          }}
          onClose={() => setCloning(null)}
          onCloned={(id) => void navigate(`/urls/${id}`)}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        title="Delete URL"
        body={
          deleting === null
            ? ''
            : `Delete ${deleting.fullUrl}? Traffic arriving on it will fall through to the fallback. Its traffic history is kept for reporting.`
        }
        confirmLabel="Delete URL"
        busy={deleteUrl.isPending}
        onClose={() => setDeleting(null)}
        onConfirm={async () => {
          if (deleting === null) return;
          await deleteUrl.mutateAsync({
            entryId: deleting.entryId,
            domainId: deleting.domainId,
          });
          setDeleting(null);
        }}
      />
    </section>
  );
}
